import React, { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { unwrap, unwrapArray } from '../lib/api-envelope';
import { Plus, X, Loader2 } from 'lucide-react';
import { MAX_SPEC_TEXT_LENGTH } from '../constants/spec-text';

interface TopDriveThreadSelectProps {
  value: string;
  onChange: (value: string) => void;
  className?: string;
}

export default function TopDriveThreadSelect({ value, onChange, className }: TopDriveThreadSelectProps) {
  const queryClient = useQueryClient();
  const [showNew, setShowNew] = useState(false);
  const [newName, setNewName] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const { data: types, isLoading } = useQuery({
    queryKey: ['top-drive-threads'],
    queryFn: async () => {
      const res = await api.lookups.topDriveThreads();
      return unwrapArray<{ id: string; name: string; is_default: boolean }>(res.data);
    },
  });

  const addMutation = useMutation({
    mutationFn: (name: string) => api.lookups.createTopDriveThread(name),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['top-drive-threads'] });
      // `created.name` on the envelope is undefined, and onChange(undefined)
      // silently clears the caller's field after a create that succeeded. Only
      // adopt a name the server actually sent; the invalidate above refreshes
      // the list either way.
      const created = unwrap<{ name?: string }>(res.data);
      if (created?.name) onChange(created.name);
      setShowNew(false);
      setNewName('');
    },
  });

  useEffect(() => {
    if (showNew && inputRef.current) {
      inputRef.current.focus();
    }
  }, [showNew]);

  const handleAdd = () => {
    const trimmed = newName.trim();
    if (!trimmed) return;
    addMutation.mutate(trimmed);
  };

  const inputClass = className || 'w-full bg-fieldiq-dark rounded-lg px-3 py-2.5 text-sm border border-fieldiq-border focus:border-fieldiq-accent outline-none';

  if (isLoading) {
    return (
      <div className={inputClass + ' flex items-center gap-2 text-fieldiq-muted'}>
        <Loader2 size={14} className="animate-spin" /> Loading...
      </div>
    );
  }

  if (showNew) {
    return (
      <div className="flex gap-2">
        <input
          ref={inputRef}
          type="text"
          value={newName}
          onChange={e => setNewName(e.target.value.toUpperCase())}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAdd(); } }}
          placeholder="e.g. DS40, XT39"
          className={inputClass + ' flex-1'}
          maxLength={MAX_SPEC_TEXT_LENGTH}
        />
        <button
          type="button"
          onClick={handleAdd}
          disabled={addMutation.isPending || !newName.trim()}
          className="px-3 py-2 bg-fieldiq-accent text-fieldiq-dark rounded-lg text-sm font-semibold hover:bg-amber-400 transition-colors disabled:opacity-50"
        >
          {addMutation.isPending ? <Loader2 size={14} className="animate-spin" /> : 'Add'}
        </button>
        <button
          type="button"
          onClick={() => { setShowNew(false); setNewName(''); }}
          className="px-2 py-2 text-fieldiq-muted hover:text-white transition-colors"
        >
          <X size={16} />
        </button>
      </div>
    );
  }

  return (
    <div className="flex gap-2">
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        className={inputClass + ' flex-1'}
      >
        <option value="">Select top drive...</option>
        {(types || []).map(t => (
          <option key={t.id} value={t.name}>{t.name}</option>
        ))}
      </select>
      <button
        type="button"
        onClick={() => setShowNew(true)}
        className="px-3 py-2 bg-fieldiq-card border border-fieldiq-border rounded-lg text-sm text-fieldiq-accent font-medium hover:border-fieldiq-accent transition-colors flex items-center gap-1 whitespace-nowrap"
      >
        <Plus size={14} /> New
      </button>
    </div>
  );
}
